import React, { useEffect, useMemo, useState } from "react";
import { Button, CircularProgress } from "@mui/material";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import { toast } from "react-toastify";
import styles from "./RevenueTracker.module.css";
import { fmt, fmtBillAmount, fmtBillDate } from "./format";

const CURRENCIES = ["INR", "USD"];

// Aging bands get progressively louder — the 90+ column is the one that
// needs a phone call, not another reminder email.
const AGE_TONE = {
  "0_30": styles.ageFresh,
  "31_60": styles.ageWarm,
  "61_90": styles.ageHot,
  "90_plus": styles.ageCritical,
};

// Columns admins can sort by, mapped to the row-field they read from and
// a comparator hint (number vs string). Age sorts on days_outstanding so
// clicking "Age" gives the intuitive "biggest debt first" behaviour
// regardless of which bucket the row falls in.
const BILLS_COLUMNS = [
  { key: "created_at", label: "Raised", type: "date" },
  { key: "days_outstanding", label: "Age", type: "number", numeric: true },
  { key: "customer_email", label: "Customer", type: "string" },
  { key: "workspace_name", label: "Workspace", type: "string" },
  { key: "domain", label: "Domain", type: "string" },
  { key: "plan_name", label: "Plan", type: "string" },
  { key: "sales_order_number", label: "Sales Order", type: "string" },
  { key: "status", label: "Status", type: "string" },
  { key: "amount", label: "Amount", type: "number", numeric: true },
];

function compareRows(a, b, sortKey, sortDir, colType) {
  const av = a?.[sortKey];
  const bv = b?.[sortKey];
  // Nulls / undefineds always sink to the bottom regardless of direction —
  // so an ascending sort on Domain doesn't show "—" rows first.
  const aMissing = av == null || av === "";
  const bMissing = bv == null || bv === "";
  if (aMissing && bMissing) return 0;
  if (aMissing) return 1;
  if (bMissing) return -1;
  let cmp;
  if (colType === "number") {
    cmp = Number(av) - Number(bv);
  } else if (colType === "date") {
    cmp = new Date(av).getTime() - new Date(bv).getTime();
  } else {
    cmp = String(av).toLowerCase().localeCompare(String(bv).toLowerCase());
  }
  return sortDir === "asc" ? cmp : -cmp;
}

function BillsTable({ code, rows, ageTone, totalAmount }) {
  // Preserve the "oldest debt first" default the page already promised in
  // the sub-header — that's created_at ascending. Clicking any header
  // toggles asc/desc; clicking a different column resets to that
  // column's most-useful default (numeric cols descending, everything
  // else ascending).
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (col) => {
    if (col.key === sortKey) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(col.key);
      setSortDir(col.numeric ? "desc" : "asc");
    }
  };

  const sortedRows = useMemo(() => {
    const col = BILLS_COLUMNS.find((c) => c.key === sortKey);
    const type = col?.type || "string";
    // Slice so we don't mutate the parent-owned array — otherwise a
    // re-render can trigger a re-sort mid-flight and get the wrong order.
    return [...rows].sort((a, b) => compareRows(a, b, sortKey, sortDir, type));
  }, [rows, sortKey, sortDir]);

  const arrow = (col) => {
    if (col.key !== sortKey) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  return (
    <div className={styles.billsTableScroll}>
      <table className={styles.billsTable}>
        <thead>
          <tr>
            {BILLS_COLUMNS.map((col) => (
              <th
                key={col.key}
                onClick={() => handleSort(col)}
                style={{
                  cursor: "pointer",
                  userSelect: "none",
                  textAlign: col.numeric ? "right" : "left",
                  color: col.key === sortKey ? "#134f8b" : undefined,
                }}
                title={`Sort by ${col.label}`}
              >
                {col.label}
                <span
                  style={{
                    fontSize: "0.75em",
                    color: col.key === sortKey ? "#134f8b" : "#8696aa",
                  }}
                >
                  {arrow(col)}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r) => {
            // Row is clickable if we know the workspace — a bill whose sub
            // has been deleted (workspace_id null) has nowhere to jump to.
            const jumpHref = r.workspace_id
              ? `/dashboard/myapps?workspaceId=${r.workspace_id}${
                  r.subscription_id
                    ? `&highlightSub=${r.subscription_id}`
                    : ""
                }`
              : null;
            const openInNewTab = () => {
              if (!jumpHref) return;
              window.open(jumpHref, "_blank", "noopener,noreferrer");
            };
            return (
              <tr
                key={r.bill_id}
                onClick={openInNewTab}
                onKeyDown={(e) => {
                  if (jumpHref && (e.key === "Enter" || e.key === " ")) {
                    e.preventDefault();
                    openInNewTab();
                  }
                }}
                tabIndex={jumpHref ? 0 : -1}
                title={
                  jumpHref
                    ? "Open workspace + highlight this sub in a new tab"
                    : "Sub not resolvable — no workspace to open"
                }
                style={{
                  cursor: jumpHref ? "pointer" : "default",
                }}
              >
                <td>{fmtBillDate(r.created_at)}</td>
                <td className={styles.billsNumeric}>
                  <span
                    className={`${styles.ageChip} ${ageTone[r.age_bucket] || ""}`}
                  >
                    {r.days_outstanding}d
                  </span>
                </td>
                <td className={styles.billsEmail}>
                  {r.customer_email || (
                    <span className={styles.billsMuted}>—</span>
                  )}
                </td>
                <td>
                  {r.workspace_name || (
                    <span className={styles.billsMuted}>—</span>
                  )}
                </td>
                <td>
                  {r.domain || <span className={styles.billsMuted}>—</span>}
                </td>
                <td>
                  {r.plan_name || (
                    <span className={styles.billsMuted}>—</span>
                  )}
                </td>
                <td>
                  {r.sales_order_number || r.sales_order_id || (
                    <span className={styles.billsMuted}>—</span>
                  )}
                </td>
                <td>
                  <span className={styles.billsTag}>{r.status}</span>
                  {r.awaiting_verification ? (
                    <span
                      className={`${styles.billsTag} ${styles.billsTagVerify}`}
                    >
                      awaiting verification
                    </span>
                  ) : null}
                  {r.sub_status &&
                  String(r.sub_status).toLowerCase() === "cancelled" ? (
                    <span
                      className={`${styles.billsTag} ${styles.billsTagMissing}`}
                    >
                      sub cancelled
                    </span>
                  ) : null}
                </td>
                <td className={styles.billsNumeric}>
                  {fmtBillAmount(r.amount, code)}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className={styles.billsSubtotalRow}>
            {/* colSpan is BILLS_COLUMNS.length - 1 so the last cell holds
                the amount total. Recomputes if columns change so it never
                drifts out of sync. */}
            <td colSpan={BILLS_COLUMNS.length - 1}>
              Total · {rows.length} bill{rows.length === 1 ? "" : "s"}
            </td>
            <td className={styles.billsNumeric}>
              {fmtBillAmount(
                totalAmount != null
                  ? totalAmount
                  : rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0),
                code,
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// Hierarchical rollup: workspace (customer) → subscription → bill.
// Same rows the flat table uses; grouped client-side so the backend
// doesn't need a second endpoint. Biggest total first at every level
// — a chase list, not an alphabetical index.
function OverdueHierarchy({ code, rows, ageTone, totalAmount }) {
  const [expandedWorkspaces, setExpandedWorkspaces] = useState(new Set());
  const [expandedSubs, setExpandedSubs] = useState(new Set());

  const toggleFrom = (set, setSet) => (key) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const toggleWorkspace = toggleFrom(expandedWorkspaces, setExpandedWorkspaces);
  const toggleSub = toggleFrom(expandedSubs, setExpandedSubs);

  const tree = useMemo(() => {
    // workspace_id → { name, email, workspace_id, total, count, subs: Map<sub_key, node> }
    // Bills whose sub was deleted (workspace_id null) get bucketed under
    // a synthetic "Unlinked" workspace so nothing is silently dropped.
    const wsMap = new Map();
    for (const r of rows) {
      const wsKey = r.workspace_id != null ? String(r.workspace_id) : "__unlinked__";
      let ws = wsMap.get(wsKey);
      if (!ws) {
        ws = {
          key: wsKey,
          workspace_id: r.workspace_id || null,
          workspace_name: r.workspace_name || "Unlinked (sub deleted)",
          customer_email: r.customer_email || null,
          total: 0,
          count: 0,
          subs: new Map(),
        };
        wsMap.set(wsKey, ws);
      }
      const amt = Number(r.amount) || 0;
      ws.total += amt;
      ws.count += 1;

      const subKey =
        r.subscription_id != null ? String(r.subscription_id) : `${wsKey}::orphan`;
      let sub = ws.subs.get(subKey);
      if (!sub) {
        sub = {
          key: subKey,
          subscription_id: r.subscription_id || null,
          plan_name: r.plan_name || (r.subscription_id ? "Unknown plan" : "Bills with no sub"),
          domain: r.domain || null,
          sub_status: r.sub_status || null,
          total: 0,
          count: 0,
          bills: [],
        };
        ws.subs.set(subKey, sub);
      }
      sub.total += amt;
      sub.count += 1;
      sub.bills.push(r);
    }
    // Freeze maps into arrays, sort biggest-total first at every level.
    return Array.from(wsMap.values())
      .map((ws) => ({
        ...ws,
        subs: Array.from(ws.subs.values())
          .map((sub) => ({
            ...sub,
            bills: [...sub.bills].sort(
              (a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0),
            ),
          }))
          .sort((a, b) => b.total - a.total),
      }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  const openWorkspaceTab = (workspaceId, subscriptionId) => {
    if (!workspaceId) return;
    const href = `/dashboard/myapps?workspaceId=${workspaceId}${
      subscriptionId ? `&highlightSub=${subscriptionId}` : ""
    }`;
    window.open(href, "_blank", "noopener,noreferrer");
  };

  const rowStyle = { cursor: "pointer" };
  const chevronStyle = {
    display: "inline-block",
    width: 14,
    textAlign: "center",
    color: "#8696aa",
    userSelect: "none",
  };
  const openBtnStyle = {
    marginLeft: 8,
    padding: "1px 8px",
    fontSize: 11,
    border: "1px solid #cfd8e5",
    background: "#f7fbff",
    color: "#134f8b",
    borderRadius: 4,
    cursor: "pointer",
  };

  return (
    <div className={styles.billsTableScroll}>
      <table className={styles.billsTable}>
        <thead>
          <tr>
            <th>Customer / Subscription / Bill</th>
            <th style={{ textAlign: "right" }}>Bills</th>
            <th style={{ textAlign: "right" }}>Outstanding</th>
          </tr>
        </thead>
        <tbody>
          {tree.map((ws) => {
            const wsOpen = expandedWorkspaces.has(ws.key);
            return (
              <React.Fragment key={ws.key}>
                <tr
                  style={{ ...rowStyle, background: "#f2f6fb" }}
                  onClick={() => toggleWorkspace(ws.key)}
                >
                  <td style={{ fontWeight: 600 }}>
                    <span style={chevronStyle}>{wsOpen ? "▾" : "▸"}</span>{" "}
                    {ws.workspace_name}
                    {ws.customer_email ? (
                      <span
                        style={{
                          color: "#607084",
                          fontWeight: 400,
                          marginLeft: 8,
                          fontSize: "0.85em",
                        }}
                      >
                        · {ws.customer_email}
                      </span>
                    ) : null}
                    {ws.workspace_id ? (
                      <button
                        type="button"
                        style={openBtnStyle}
                        onClick={(e) => {
                          e.stopPropagation();
                          openWorkspaceTab(ws.workspace_id, null);
                        }}
                        title="Open workspace in a new tab"
                      >
                        Open ↗
                      </button>
                    ) : null}
                  </td>
                  <td className={styles.billsNumeric}>{ws.count}</td>
                  <td className={styles.billsNumeric} style={{ fontWeight: 600 }}>
                    {fmtBillAmount(ws.total, code)}
                  </td>
                </tr>
                {wsOpen &&
                  ws.subs.map((sub) => {
                    const subOpen = expandedSubs.has(`${ws.key}::${sub.key}`);
                    const subRowKey = `${ws.key}::${sub.key}`;
                    return (
                      <React.Fragment key={subRowKey}>
                        <tr
                          style={{ ...rowStyle, background: "#fbfcfe" }}
                          onClick={() => toggleSub(subRowKey)}
                        >
                          <td style={{ paddingLeft: 32 }}>
                            <span style={chevronStyle}>
                              {subOpen ? "▾" : "▸"}
                            </span>{" "}
                            {sub.plan_name}
                            {sub.domain ? (
                              <span
                                style={{
                                  color: "#607084",
                                  marginLeft: 8,
                                  fontSize: "0.85em",
                                }}
                              >
                                · {sub.domain}
                              </span>
                            ) : null}
                            {sub.sub_status &&
                            String(sub.sub_status).toLowerCase() ===
                              "cancelled" ? (
                              <span
                                className={`${styles.billsTag} ${styles.billsTagMissing}`}
                                style={{ marginLeft: 8 }}
                              >
                                sub cancelled
                              </span>
                            ) : null}
                            {ws.workspace_id && sub.subscription_id ? (
                              <button
                                type="button"
                                style={openBtnStyle}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openWorkspaceTab(
                                    ws.workspace_id,
                                    sub.subscription_id,
                                  );
                                }}
                                title="Open workspace and highlight this sub"
                              >
                                Open ↗
                              </button>
                            ) : null}
                          </td>
                          <td className={styles.billsNumeric}>{sub.count}</td>
                          <td className={styles.billsNumeric}>
                            {fmtBillAmount(sub.total, code)}
                          </td>
                        </tr>
                        {subOpen &&
                          sub.bills.map((b) => (
                            <tr key={b.bill_id} style={{ background: "#ffffff" }}>
                              <td style={{ paddingLeft: 64, color: "#4b5563" }}>
                                <span style={{ color: "#8696aa", marginRight: 6 }}>
                                  ↳
                                </span>
                                {fmtBillDate(b.created_at)}
                                <span
                                  className={`${styles.ageChip} ${ageTone[b.age_bucket] || ""}`}
                                  style={{ marginLeft: 10 }}
                                >
                                  {b.days_outstanding}d
                                </span>
                                {b.sales_order_number || b.sales_order_id ? (
                                  <span
                                    style={{
                                      marginLeft: 10,
                                      color: "#607084",
                                      fontSize: "0.85em",
                                    }}
                                  >
                                    · SO {b.sales_order_number || b.sales_order_id}
                                  </span>
                                ) : null}
                                <span
                                  className={styles.billsTag}
                                  style={{ marginLeft: 8 }}
                                >
                                  {b.status}
                                </span>
                                {b.awaiting_verification ? (
                                  <span
                                    className={`${styles.billsTag} ${styles.billsTagVerify}`}
                                    style={{ marginLeft: 6 }}
                                  >
                                    awaiting verification
                                  </span>
                                ) : null}
                              </td>
                              <td className={styles.billsNumeric}>1</td>
                              <td className={styles.billsNumeric}>
                                {fmtBillAmount(b.amount, code)}
                              </td>
                            </tr>
                          ))}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
        <tfoot>
          <tr className={styles.billsSubtotalRow}>
            <td colSpan={2}>
              Total · {tree.length} customer{tree.length === 1 ? "" : "s"}
            </td>
            <td className={styles.billsNumeric}>
              {fmtBillAmount(
                totalAmount != null
                  ? totalAmount
                  : rows.reduce((s, r) => s + (Number(r.amount) || 0), 0),
                code,
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

const OverdueAmount = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/revenue/overdue`,
        { credentials: "include" },
      );
      const json = await res.json();
      if (json?.status === 200) setData(json.data);
      else toast.error(json?.msg || "Failed to load overdue amount");
    } catch (e) {
      console.error("[revenue:overdue]", e);
      toast.error("Failed to load overdue amount");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ageBuckets = useMemo(
    () =>
      data?.age_buckets || [
        { key: "0_30", label: "0–30 days" },
        { key: "31_60", label: "31–60 days" },
        { key: "61_90", label: "61–90 days" },
        { key: "90_plus", label: "90+ days" },
      ],
    [data],
  );

  // Currencies that actually owe us something, plus anything the backend
  // returned — so an empty USD book renders as "nothing outstanding"
  // rather than vanishing.
  const currencies = useMemo(() => {
    const found = Object.keys(data?.by_currency || {});
    return Array.from(new Set([...CURRENCIES, ...found]));
  }, [data]);

  return (
    <div>
      <div className={styles.header}>
        <div className={styles.titleBlock}>
          <h1>Overdue Amount</h1>
          <div className={styles.subtitle}>
            Every bill we have raised and never collected — all-time, not
            bound to the month picker. A bill counts as owed when it is not
            paid / void / cancelled / refunded, AND has no successful
            payment row, AND has no invoice row. Oldest debt first.
          </div>
        </div>
        <div className={styles.controls}>
          <Button
            className={styles.refreshBtn}
            startIcon={<RefreshOutlinedIcon />}
            onClick={fetchData}
          >
            Refresh
          </Button>
        </div>
      </div>

      {loading && !data ? (
        <div className={styles.loaderWrap}>
          <CircularProgress />
        </div>
      ) : (
        <>
          <div className={styles.currencyRow}>
            {currencies.map((code) => {
              const block = data?.by_currency?.[code];
              const total = Number(block?.total || 0);
              const count = Number(block?.bill_count || 0);
              const writtenOff = Number(data?.written_off?.[code] || 0);
              return (
                <div key={code} className={styles.currencyCard}>
                  <div
                    className={`${styles.currencyHeader} ${styles.overdueHeader}`}
                  >
                    <div className={styles.currencyLabel}>
                      {code} · pending for payment
                    </div>
                    <div className={styles.currencyMargin}>
                      {fmt(total, code)}
                    </div>
                    <div className={styles.currencyTopline}>
                      <span>
                        Bills: <strong>{count}</strong>
                      </span>
                      {writtenOff > 0 ? (
                        <span>
                          Written off:{" "}
                          <strong>{fmt(writtenOff, code)}</strong>
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className={styles.bucketSection}>
                    <div className={styles.bucketSectionTitle}>
                      By age of debt
                    </div>
                    {ageBuckets.map((b) => {
                      const cell = block?.by_age?.[b.key] || {
                        amount: 0,
                        count: 0,
                      };
                      return (
                        <div key={b.key} className={styles.bucketRow}>
                          <div>
                            <div className={styles.bucketLabel}>
                              <span
                                className={`${styles.ageDot} ${AGE_TONE[b.key] || ""}`}
                              />
                              {b.label}
                            </div>
                            <span className={styles.bucketHint}>
                              {cell.count} bill{cell.count === 1 ? "" : "s"}
                            </span>
                          </div>
                          <div className={styles.bucketCount}></div>
                          <div className={styles.bucketValue}>
                            {fmt(cell.amount, code)}
                          </div>
                        </div>
                      );
                    })}
                    <div className={styles.totalRow}>
                      <div className={styles.totalLabel}>Total outstanding</div>
                      <div></div>
                      <div className={styles.totalValue}>
                        {fmt(total, code)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {data?.debug ? (
            <div className={styles.debugNote}>
              Scanned {data.debug.bills_scanned} unsettled bill
              {data.debug.bills_scanned === 1 ? "" : "s"}
              {data.debug.settled_by_payment
                ? ` · ${data.debug.settled_by_payment} dropped (payment row found)`
                : ""}
              {data.debug.settled_by_invoice
                ? ` · ${data.debug.settled_by_invoice} dropped (invoice row found)`
                : ""}
            </div>
          ) : null}

          <div className={styles.billsSection}>
            <h2 className={styles.billsTitle}>Outstanding bills</h2>
            <p className={styles.billsSub}>
              One row per uncollected bill, oldest first. "Awaiting
              verification" means the customer has submitted a UTR from the
              dashboard and an admin still has to accept it.
            </p>
            {currencies.map((code) => {
              const rows = data?.by_currency?.[code]?.rows || [];
              return (
                <div key={code} className={styles.billsCategory}>
                  <div className={styles.billsCategoryHeader}>
                    {code} · {rows.length} bill{rows.length === 1 ? "" : "s"}
                  </div>
                  {rows.length === 0 ? (
                    <div className={styles.billsEmpty}>
                      Nothing outstanding in {code}.
                    </div>
                  ) : (
                    <>
                      <BillsTable
                        code={code}
                        rows={rows}
                        ageTone={AGE_TONE}
                        totalAmount={data?.by_currency?.[code]?.total || 0}
                      />
                      <h3
                        className={styles.billsTitle}
                        style={{ marginTop: 24, fontSize: 16 }}
                      >
                        {code} · By customer → subscription → bill
                      </h3>
                      <p className={styles.billsSub}>
                        Same bills, rolled up by workspace and then by
                        subscription. Click a row to expand; click "Open ↗"
                        to jump into that workspace or sub in a new tab.
                      </p>
                      <OverdueHierarchy
                        code={code}
                        rows={rows}
                        ageTone={AGE_TONE}
                        totalAmount={data?.by_currency?.[code]?.total || 0}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default OverdueAmount;
